import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cognitoAuthService } from '@/services/cognito-auth.service'
import { useAuthStore } from '@/stores/auth.store'
import { ApiError } from '@/lib/api-client'
import { UserRole } from '@shared/types/roles'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import logo from '@/assets/logo.svg'

const loginSchema = z.object({
  email: z.string().email('validation.emailInvalid'),
  password: z.string().min(1, 'validation.required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const setAuth = useAuthStore((state) => state.setAuth)

  const registrationMessage = location.state?.message

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const mutation = useMutation({
    mutationFn: (data: LoginFormData) => cognitoAuthService.login(data.email, data.password),
    onSuccess: (authResult) => {
      setAuth(authResult.user, authResult.token, authResult.refreshToken)

      // Redirect based on role
      if (authResult.user.role === UserRole.ADMIN) {
        navigate('/admin/applicants')
      } else if (authResult.user.role === UserRole.SCOUT) {
        navigate('/scout/dashboard')
      } else {
        navigate('/applicant/dashboard')
      }
    },
  })

  const onSubmit = (data: LoginFormData) => {
    mutation.mutate(data, {
      onError: (error: Error) => {
        // Handle "User is not confirmed" error
        if (error.message.includes('not confirmed')) {
          navigate('/verify-email', { state: { email: data.email } })
        }
      },
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>
        <div className="flex justify-center mb-6">
          <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
            <img src={logo} alt="U-Like-It" className="h-16 w-auto hover:opacity-80 transition-opacity cursor-pointer" />
          </a>
        </div>
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
          {t('app.title')}
        </h1>
        <p className="text-center text-gray-600 mb-8">
          {t('auth.login')}
        </p>

        {/* Success Message from Registration */}
        {registrationMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">{registrationMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.email')}
            </label>
            <input
              {...register('email')}
              type="email"
              id="email"
              autoComplete="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{t(errors.email.message || 'validation.emailInvalid')}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password')}
            </label>
            <input
              {...register('password')}
              type="password"
              id="password"
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{t(errors.password.message || 'validation.required')}</p>
            )}
          </div>

          {/* Error Message */}
          {mutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : t('common.error')}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          {t('auth.noAccount')}{' '}
          <a href="/register" className="text-blue-600 hover:text-blue-700 font-medium">
            {t('auth.registerNow')}
          </a>
        </p>
      </div>
    </div>
  )
}
